'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type MANAInterest =
  | 'marriage-family' |'history-heritage' |'legacy-profiles' |'youth-young-adults' |'faith-ramadan' |'justice-civic';

interface InterestConfig {
  id: MANAInterest;
  label: string;
  arabic: string;
  description: string;
  color: string;
  bg: string;
  icon: string;
  keywords: string[];
}

// ─── INTEREST CATEGORIES ──────────────────────────────────────────────────────

const INTEREST_CATEGORIES: InterestConfig[] = [
  {
    id: 'marriage-family',
    label: 'Marriage & Family',
    arabic: 'الزواج والأسرة',
    description: 'Strengthening Muslim families, marriage guidance, parenting, and household dynamics',
    color: '#c47a7a',
    bg: 'rgba(196,122,122,0.1)',
    icon: '◈',
    keywords: ['marriage', 'family', 'parenting', 'nikah', 'children'],
  },
  {
    id: 'history-heritage',
    label: 'History & Heritage',
    arabic: 'التاريخ والتراث',
    description: 'African-American Muslim history, cultural heritage, and community legacy',
    color: '#c9a84c',
    bg: 'rgba(201,168,76,0.1)',
    icon: '⬡',
    keywords: ['history', 'heritage', 'legacy', 'African American', 'Black Muslim'],
  },
  {
    id: 'legacy-profiles',
    label: 'Legacy & Profiles',
    arabic: 'الإرث والشخصيات',
    description: 'Profiles of Muslim leaders, scholars, imams, and community builders',
    color: '#8a8ab8',
    bg: 'rgba(138,138,184,0.1)',
    icon: '✦',
    keywords: ['profile', 'leader', 'imam', 'scholar', 'biography'],
  },
  {
    id: 'youth-young-adults',
    label: 'Youth & Young Adults',
    arabic: 'الشباب',
    description: 'Programs, resources, and guidance for Muslim youth ages 5–34',
    color: '#2d8a87',
    bg: 'rgba(45,138,135,0.1)',
    icon: '◎',
    keywords: ['youth', 'young adults', 'student', 'campus', 'MSA'],
  },
  {
    id: 'faith-ramadan',
    label: 'Faith & Ramadan',
    arabic: 'الإيمان ورمضان',
    description: 'Spiritual development, Islamic practice, Ramadan, and worship',
    color: '#D4A82A',
    bg: 'rgba(212,168,42,0.1)',
    icon: '☽',
    keywords: ['faith', 'ramadan', 'prayer', 'quran', 'spiritual'],
  },
  {
    id: 'justice-civic',
    label: 'Justice & Civic Life',
    arabic: 'العدالة والمدنية',
    description: 'Justice advocacy, civic engagement, policy, and community organizing',
    color: '#2d7a47',
    bg: 'rgba(45,122,71,0.1)',
    icon: '⚖',
    keywords: ['justice', 'civic', 'voting', 'policy', 'advocacy'],
  },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

interface SubscriberProfileEditorProps {
  compact?: boolean;
}

export default function SubscriberProfileEditor({ compact = false }: SubscriberProfileEditorProps) {
  const supabase = createClient();

  const [user, setUser] = useState<any>(null);
  const [selectedInterests, setSelectedInterests] = useState<MANAInterest[]>([]);
  const [savedInterests, setSavedInterests] = useState<MANAInterest[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [email, setEmail] = useState('');

  // Load user + existing interests
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        setEmail(user.email || '');
        const { data } = await supabase
          .from('subscriber_interests')
          .select('interests')
          .eq('user_id', user.id)
          .single();
        if (data?.interests) {
          setSelectedInterests(data.interests as MANAInterest[]);
          setSavedInterests(data.interests as MANAInterest[]);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const toggleInterest = useCallback((id: MANAInterest) => {
    setSelectedInterests(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
    setSaveStatus('idle');
  }, []);

  const handleSave = async () => {
    if (!user && !email) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const payload = {
        interests: selectedInterests,
        updated_at: new Date().toISOString(),
        ...(user ? { user_id: user.id, email: user.email } : { email }),
      };

      const { error } = user
        ? await supabase
            .from('subscriber_interests')
            .upsert(payload, { onConflict: 'user_id' })
        : await supabase
            .from('subscriber_interests')
            .upsert(payload, { onConflict: 'email' });

      if (error) throw error;
      setSavedInterests([...selectedInterests]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(selectedInterests.sort()) !== JSON.stringify(savedInterests.sort());

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em' }}>
          Loading preferences…
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: compact ? 'transparent' : 'rgba(6,13,18,0.95)',
      border: compact ? 'none' : '1px solid rgba(201,168,76,0.15)',
      borderRadius: compact ? 0 : 10,
      padding: compact ? 0 : 32,
    }}>
      {/* Header */}
      {!compact && (
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>
            ✦ MANA Newsletter · Subscriber Preferences
          </p>
          <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#F5F0E8', fontWeight: 400, marginBottom: 10 }}>
            Your Newsletter Interests
          </h2>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, maxWidth: 560 }}>
            Select the topics that matter most to you. Your newsletter will be automatically filtered to include only articles from your chosen categories — delivered to your inbox with relevant content from MANA's verified partner network.
          </p>
        </div>
      )}

      {/* Guest email input */}
      {!user && (
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.5)', display: 'block', marginBottom: 8 }}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 6,
              padding: '10px 14px',
              fontFamily: 'monospace',
              fontSize: 12,
              color: '#F5F0E8',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      {/* Interest grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 12,
        marginBottom: 28,
      }}>
        {INTEREST_CATEGORIES.map(cat => {
          const selected = selectedInterests.includes(cat.id);
          return (
            <button
              key={cat.id}
              onClick={() => toggleInterest(cat.id)}
              style={{
                background: selected ? cat.bg : 'rgba(255,255,255,0.02)',
                border: `1px solid ${selected ? cat.color + '55' : 'rgba(255,255,255,0.07)'}`,
                borderLeft: `3px solid ${selected ? cat.color : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 8,
                padding: '16px 18px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.18s',
                position: 'relative',
              }}
            >
              {/* Selected indicator */}
              {selected && (
                <div style={{
                  position: 'absolute',
                  top: 10,
                  right: 12,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: cat.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  color: '#060d12',
                  fontWeight: 700,
                }}>✓</div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 16, color: selected ? cat.color : 'rgba(212,197,169,0.3)' }}>{cat.icon}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: selected ? cat.color : 'rgba(212,197,169,0.5)' }}>
                  {cat.label}
                </span>
              </div>

              <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: selected ? 'rgba(212,197,169,0.5)' : 'rgba(212,197,169,0.25)', marginBottom: 6 }}>
                {cat.arabic}
              </div>

              {!compact && (
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: selected ? 'rgba(212,197,169,0.65)' : 'rgba(212,197,169,0.35)', lineHeight: 1.6, margin: 0 }}>
                  {cat.description}
                </p>
              )}

              {/* Keyword tags */}
              {!compact && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                  {cat.keywords.slice(0, 3).map(kw => (
                    <span key={kw} style={{
                      fontFamily: 'monospace',
                      fontSize: 7,
                      padding: '2px 6px',
                      borderRadius: 3,
                      background: selected ? `${cat.color}15` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${selected ? cat.color + '30' : 'rgba(255,255,255,0.06)'}`,
                      color: selected ? cat.color : 'rgba(212,197,169,0.3)',
                      letterSpacing: '.06em',
                    }}>{kw}</span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selection summary */}
      <div style={{
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.12)',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.6)', letterSpacing: '.08em' }}>
          {selectedInterests.length === 0
            ? 'No interests selected — you will receive the full general newsletter'
            : `${selectedInterests.length} of 6 categories selected — newsletter will be filtered to your interests`}
        </span>
        {selectedInterests.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {selectedInterests.map(id => {
              const cat = INTEREST_CATEGORIES.find(c => c.id === id)!;
              return (
                <span key={id} style={{
                  fontFamily: 'monospace',
                  fontSize: 7,
                  padding: '2px 8px',
                  borderRadius: 3,
                  background: cat.bg,
                  border: `1px solid ${cat.color}40`,
                  color: cat.color,
                  letterSpacing: '.06em',
                }}>{cat.label}</span>
              );
            })}
          </div>
        )}
      </div>

      {/* Save button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={handleSave}
          disabled={saving || (!hasChanges && saveStatus !== 'idle') || (!user && !email)}
          style={{
            fontFamily: 'monospace',
            fontSize: 10,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            padding: '10px 24px',
            borderRadius: 5,
            cursor: saving || (!hasChanges && saveStatus === 'saved') ? 'not-allowed' : 'pointer',
            border: '1px solid rgba(201,168,76,0.4)',
            background: saving ? 'rgba(201,168,76,0.05)' : 'rgba(201,168,76,0.12)',
            color: saving ? 'rgba(201,168,76,0.4)' : '#c9a84c',
            transition: 'all 0.15s',
          }}
        >
          {saving ? '◌ Saving…' : saveStatus === 'saved' ? '✓ Saved' : '◈ Save Preferences'}
        </button>

        {saveStatus === 'saved' && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d7a47', letterSpacing: '.06em' }}>
            ✓ Your newsletter will now be filtered by your selected interests
          </span>
        )}
        {saveStatus === 'error' && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#c47a7a', letterSpacing: '.06em' }}>
            ✗ Error saving — please try again
          </span>
        )}
      </div>
    </div>
  );
}

export { INTEREST_CATEGORIES };
export type { MANAInterest };
